import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Download, Smartphone } from 'lucide-react';
import { hasFeature } from '../utils/features';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function PWAInstallBanner({ user }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      trackInstall('already_installed');
      return;
    }

    // Check if user has the PWA feature
    if (!hasFeature(user, 'pwa_install')) {
      return;
    }

    // Check if user dismissed the banner before
    const dismissed = localStorage.getItem('pwa_banner_dismissed');
    if (dismissed) {
      const dismissedTime = new Date(dismissed);
      const hoursSinceDismiss = (Date.now() - dismissedTime) / (1000 * 60 * 60);
      // Show again after 24 hours
      if (hoursSinceDismiss < 24) {
        return;
      }
    }

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if app was installed
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowBanner(false);
      trackInstall('installed');
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [user]);

  const trackInstall = async (status) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      await fetch(`${API_URL}/api/pwa/track-install`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status,
          device: navigator.userAgent,
          platform: navigator.platform
        })
      });
    } catch (error) {
      console.error('Failed to track PWA install:', error);
    }
  };

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    // Show install prompt
    deferredPrompt.prompt();
    
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      trackInstall('accepted');
    } else {
      trackInstall('dismissed');
    }
    
    setDeferredPrompt(null);
    setShowBanner(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa_banner_dismissed', new Date().toISOString());
    setShowBanner(false);
    trackInstall('banner_dismissed');
  };

  if (!showBanner || isInstalled || !hasFeature(user, 'pwa_install')) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 shadow-lg z-50 animate-slide-up" data-testid="pwa-install-banner">
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="bg-white/20 p-2 rounded-lg">
            <Smartphone className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">Install TeamTrack App</h3>
            <p className="text-sm text-white/80">Get quick access, offline support & push notifications</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={handleInstall}
            className="bg-white text-indigo-600 hover:bg-white/90"
            data-testid="pwa-install-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Install
          </Button>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={handleDismiss}
            className="text-white hover:bg-white/20"
            data-testid="pwa-dismiss-btn"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default PWAInstallBanner;
